"use client"

import React from "react"
import { CommandAction, useCommandPaletteContext } from "@/contexts/command-palette-context"
import type { ClientSearchResult, PastClientSearchResult, ProspectSearchResult, SearchResult } from "@/schema/search"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@ui/shad"
import { KeyboardShortcut } from "@ui/shad/keyboard-shortcut"

import { ALL_BOOKS_SQID, isAllBooksId } from "@/lib/all-books"
import { useFetchRecentHouseholds } from "@/hooks/api/households/queries"
import { useBookId } from "@/hooks/use-book-id"
import { useGlobalSearch } from "@/hooks/use-global-search"
import { useLoadingRouter } from "@/hooks/use-loading-router"

import { CustomDialogContent } from "../custom-dialog-content"
import { CommandFooter } from "./command-footer"
import { CommandItems } from "./command-items"
import { PreviewPane } from "./preview-pane"
import { SearchBar } from "./search-bar"
import { useNoResultsActions } from "./use-no-results-actions"
import { filterCommandActions, resultKey } from "./utils"

const COMMAND_PALETTE_ALLOWED_TYPES: SearchResult["type"][] = [
  "household",
  "client",
  "past_client",
  "prospect",
  "contact",
  "opportunity",
  "business",
  "user",
  "trust",
]

function getPreviewTargets(preview: HTMLElement) {
  return Array.from(preview.querySelectorAll<HTMLElement>("a[href], button, [role=button][tabindex]")).filter(
    (element) =>
      element.tabIndex >= 0 &&
      !element.matches(":disabled, [aria-disabled=true]") &&
      element.getClientRects().length > 0
  )
}

export function CommandPalette() {
  const { push } = useLoadingRouter({ bookScoped: false })
  const { closeDialog } = useGlobalDialogStore()
  const bookId = useBookId()
  const isAllBooks = isAllBooksId(bookId)
  const { searchQuery, results, isSearching, handleSearchChange } = useGlobalSearch({
    allowedTypes: COMMAND_PALETTE_ALLOWED_TYPES,
    bookIdOverride: ALL_BOOKS_SQID,
  })
  const { globalActions, pageActions } = useCommandPaletteContext()
  const [selectedItem, setSelectedItem] = React.useState<SearchResult | CommandAction>()
  const [selectedType, setSelectedType] = React.useState<"record" | "action" | null>(null)
  const [commandValue, setCommandValue] = React.useState<string>("")
  const paletteRef = React.useRef<HTMLDivElement>(null)
  const navigatingListRef = React.useRef(false)

  const handlePaletteKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.nativeEvent.isComposing) return

    const preview = paletteRef.current?.querySelector<HTMLElement>("[data-command-preview]")
    const input = paletteRef.current?.querySelector<HTMLInputElement>("[cmdk-input]")
    if (!(event.target instanceof HTMLElement)) return

    if (preview?.contains(event.target)) {
      if (event.key === "ArrowLeft") {
        event.preventDefault()
        event.stopPropagation()
        navigatingListRef.current = true
        input?.focus()
      } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault()
        event.stopPropagation()
        const targets = getPreviewTargets(preview)
        const currentIndex = targets.findIndex((target) => target === document.activeElement)
        const nextIndex = currentIndex < 0 ? 0 : currentIndex + (event.key === "ArrowDown" ? 1 : -1)
        targets[Math.max(0, Math.min(targets.length - 1, nextIndex))]?.focus()
      }
      return
    }

    if (event.target !== input) return
    if (event.key === "ArrowDown" || event.key === "ArrowUp") navigatingListRef.current = true
    if (event.key === "ArrowLeft" || event.key === "Home" || event.key === "End") navigatingListRef.current = false
    if (event.key === "ArrowRight" && preview && (navigatingListRef.current || !searchQuery)) {
      event.preventDefault()
      event.stopPropagation()
      const target = getPreviewTargets(preview)[0] ?? preview
      target.focus()
    }
  }

  const { data: recentHouseholdsData } = useFetchRecentHouseholds()

  const recentViewResults = React.useMemo<SearchResult[]>(() => {
    if (!recentHouseholdsData?.households) return []
    return recentHouseholdsData.households.slice(0, 5).map((h) => {
      const type: "client" | "past_client" | "prospect" =
        h.type === "PastClient" ? "past_client" : h.type === "Client" ? "client" : "prospect"
      const location = [h.city, h.state].filter(Boolean).join(", ")
      return {
        id: h.id,
        type,
        match_type: "text" as const,
        score: 100,
        display_label: h.name,
        redirect_path: `/${h.book_id}/households/${h.id}`,
        book_id: h.book_id,
        book_name: h.book_name,
        attributes: {
          tier: h.tier != null ? ["A", "B", "C", "D"][h.tier] : undefined,
          status: h.status,
          members: h.members,
          tags: h.tags,
          tag_count: h.tag_count,
          ai_summary: h.ai_summary,
          street_address: h.street_address,
          zip: h.zip,
          city: location ? h.city : undefined,
          state: location ? h.state : undefined,
        },
      } satisfies ClientSearchResult | PastClientSearchResult | ProspectSearchResult
    })
  }, [recentHouseholdsData])

  const hasResults = results.length > 0
  const hasSearchQuery = searchQuery.trim().length > 0

  const pageActionGroups = React.useMemo(() => {
    const groups = new Map<string, CommandAction[]>()
    const filtered = filterCommandActions(pageActions, searchQuery)

    filtered.forEach((action) => {
      const groupName = action.group || "Actions"
      if (!groups.has(groupName)) {
        groups.set(groupName, [])
      }
      groups.get(groupName)!.push(action)
    })

    return groups
  }, [pageActions, searchQuery])

  const navigationActions = React.useMemo(() => {
    return filterCommandActions(
      globalActions.filter((action) => action.type === "navigation"),
      searchQuery
    )
  }, [globalActions, searchQuery])

  const taskActions = React.useMemo(() => {
    return filterCommandActions(
      globalActions.filter((action) => action.type === "task"),
      searchQuery
    )
  }, [globalActions, searchQuery])

  const hasMatchingActions = pageActionGroups.size > 0 || navigationActions.length > 0 || taskActions.length > 0
  const noResultsCreateActions = useNoResultsActions(
    hasSearchQuery,
    hasResults || hasMatchingActions,
    isSearching,
    searchQuery
  )

  const handleItemSelect = React.useCallback(
    (item: SearchResult) => {
      if (item.redirect_path) {
        let path = item.redirect_path
        if (isAllBooks && item.book_id) {
          path = path.replace(/^\/[^/]+/, `/${ALL_BOOKS_SQID}`)
        }
        closeDialog()
        push(path)
      }
    },
    [push, closeDialog, isAllBooks]
  )

  const handleActionSelect = React.useCallback(
    (action: CommandAction) => {
      if (action.onEnter) {
        closeDialog()
        action.onEnter()
      } else if (action.action) {
        closeDialog()
        action.action()
      }
    },
    [closeDialog]
  )

  React.useEffect(() => {
    if (commandValue) {
      // Check page actions first
      let foundAction: CommandAction | undefined
      for (const actions of Array.from(pageActionGroups.values())) {
        foundAction = actions.find((a) => a.id === commandValue)
        if (foundAction) break
      }

      // Then check global actions
      if (!foundAction) {
        const navAction = navigationActions.find((a) => a.id === commandValue)
        const taskAction = taskActions.find((a) => a.id === commandValue)
        const noResultsAction = noResultsCreateActions.find((a) => a.id === commandValue)
        foundAction = navAction || taskAction || noResultsAction
      }

      if (foundAction) {
        setSelectedItem(foundAction)
        setSelectedType("action")
      } else {
        const record =
          results.find((r) => resultKey(r) === commandValue) ||
          recentViewResults.find((r) => resultKey(r) === commandValue)
        if (record) {
          setSelectedItem(record)
          setSelectedType("record")
        } else {
          // Clear selection if no item is found
          setSelectedItem(undefined)
          setSelectedType(null)
        }
      }
    } else {
      // Clear selection when commandValue is empty
      setSelectedItem(undefined)
      setSelectedType(null)
    }
  }, [
    commandValue,
    results,
    recentViewResults,
    navigationActions,
    taskActions,
    pageActionGroups,
    noResultsCreateActions,
  ])

  React.useEffect(() => {
    if (!hasSearchQuery) {
      // Prefer recent views as default selection
      if (recentViewResults.length > 0) {
        const firstRecent = recentViewResults[0]
        setCommandValue(resultKey(firstRecent))
        setSelectedItem(firstRecent)
        setSelectedType("record")
        return
      }

      // Get first page action if available
      let firstAction: CommandAction | undefined
      for (const actions of Array.from(pageActionGroups.values())) {
        if (actions.length > 0) {
          firstAction = actions[0]
          break
        }
      }

      // Otherwise get first global action
      if (!firstAction) {
        firstAction = navigationActions[0] || taskActions[0]
      }

      if (firstAction) {
        setCommandValue(firstAction.id)
        setSelectedItem(firstAction)
        setSelectedType("action")
      } else {
        setSelectedItem(undefined)
        setSelectedType(null)
        setCommandValue("")
      }
    }
  }, [hasSearchQuery, navigationActions, taskActions, pageActionGroups, recentViewResults])

  React.useEffect(() => {
    if (hasSearchQuery) {
      // Get first page action if available
      let firstAction: CommandAction | undefined
      for (const actions of Array.from(pageActionGroups.values())) {
        if (actions.length > 0) {
          firstAction = actions[0]
          break
        }
      }

      // Check AI action first if it exists
      if (firstAction) {
        setCommandValue(firstAction.id)
        setSelectedItem(firstAction)
        setSelectedType("action")
      } else if (results.length > 0) {
        // Prioritize search results over global actions when searching
        const firstResult = results[0]
        setCommandValue(resultKey(firstResult))
        setSelectedItem(firstResult)
        setSelectedType("record")
      } else if (noResultsCreateActions.length > 0) {
        // Show no-results create actions when no other results
        const firstNoResultsAction = noResultsCreateActions[0]
        setCommandValue(firstNoResultsAction.id)
        setSelectedItem(firstNoResultsAction)
        setSelectedType("action")
      } else {
        // Finally check global actions
        const globalAction = navigationActions[0] || taskActions[0]
        if (globalAction) {
          setCommandValue(globalAction.id)
          setSelectedItem(globalAction)
          setSelectedType("action")
        } else {
          // Clear selection if no actions or results are found
          setSelectedItem(undefined)
          setSelectedType(null)
          setCommandValue("")
        }
      }
    }
  }, [results, hasSearchQuery, navigationActions, taskActions, pageActionGroups, noResultsCreateActions])

  return (
    <CustomDialogContent title="Search and commands">
      <div ref={paletteRef} className="flex" onKeyDownCapture={handlePaletteKeyDown}>
        <div className="min-w-0 flex-1 border-r">
          <Command
            className=""
            shouldFilter={false}
            value={commandValue}
            onValueChange={setCommandValue}
            onKeyDown={(e) => {
              if (e.key === "Enter" && selectedItem) {
                e.preventDefault()
                if (selectedType === "action") {
                  handleActionSelect(selectedItem as CommandAction)
                } else if (selectedType === "record") {
                  handleItemSelect(selectedItem as SearchResult)
                }
              }
            }}
          >
            <SearchBar
              searchQuery={searchQuery}
              isSearching={isSearching}
              onSearchChange={(value) => {
                navigatingListRef.current = false
                handleSearchChange(value)
              }}
            />
            <CommandList className="h-full max-h-[340px]">
              {hasSearchQuery &&
                !hasResults &&
                navigationActions.length === 0 &&
                taskActions.length === 0 &&
                pageActionGroups.size === 0 &&
                noResultsCreateActions.length === 0 &&
                !isSearching && <CommandEmpty>No results found.</CommandEmpty>}
              {!hasSearchQuery &&
                recentViewResults.length === 0 &&
                navigationActions.length === 0 &&
                taskActions.length === 0 &&
                pageActionGroups.size === 0 && <CommandEmpty>Type to search or navigate</CommandEmpty>}

              {/* Recently viewed households shown when not searching */}
              {!hasSearchQuery && recentViewResults.length > 0 && (
                <CommandGroup heading="Recently viewed">
                  <CommandItems results={recentViewResults} onItemSelect={handleItemSelect} />
                </CommandGroup>
              )}

              {/* Page-specific actions come first with custom group titles */}
              {Array.from(pageActionGroups.entries()).map(([groupName, actions]) => (
                <CommandGroup key={groupName} heading={groupName}>
                  {actions.map((action) => (
                    <CommandItem key={action.id} value={action.id} onSelect={() => handleActionSelect(action)}>
                      <div className="flex w-full items-center justify-between">
                        <span>{action.label}</span>
                        {action.shortcut && (
                          <KeyboardShortcut keys={action.shortcut.formatted || action.shortcut.keys} />
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}

              {/* When searching, show search results before global actions */}
              {hasSearchQuery && hasResults && (
                <CommandGroup heading="Search Results">
                  <CommandItems results={results} onItemSelect={handleItemSelect} />
                </CommandGroup>
              )}

              {/* Show create actions when no results found */}
              {noResultsCreateActions.length > 0 && (
                <CommandGroup heading="Create">
                  {noResultsCreateActions.map((action) => (
                    <CommandItem key={action.id} value={action.id} onSelect={() => handleActionSelect(action)}>
                      <div className="flex w-full items-center justify-between">
                        <span>{action.label}</span>
                        {action.shortcut && (
                          <KeyboardShortcut keys={action.shortcut.formatted || action.shortcut.keys} />
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {navigationActions.length > 0 && (
                <CommandGroup heading="Navigation">
                  {navigationActions.map((action) => (
                    <CommandItem key={action.id} value={action.id} onSelect={() => handleActionSelect(action)}>
                      <div className="flex w-full items-center justify-between">
                        <span>{action.label}</span>
                        {action.shortcut && (
                          <KeyboardShortcut keys={action.shortcut.formatted || action.shortcut.keys} />
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {taskActions.length > 0 && (
                <CommandGroup heading="Actions">
                  {taskActions.map((action) => (
                    <CommandItem key={action.id} value={action.id} onSelect={() => handleActionSelect(action)}>
                      <div className="flex w-full items-center justify-between">
                        <span>{action.label}</span>
                        {action.shortcut && (
                          <KeyboardShortcut keys={action.shortcut.formatted || action.shortcut.keys} />
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* When not searching, show search results at the end */}
              {!hasSearchQuery && hasResults && (
                <CommandGroup heading="Search Results">
                  <CommandItems results={results} onItemSelect={handleItemSelect} />
                </CommandGroup>
              )}
            </CommandList>
            <CommandFooter selectedItem={selectedItem} isAction={selectedType === "action"} />
          </Command>
        </div>
        {selectedType === "record" && selectedItem ? (
          <PreviewPane selectedItem={selectedItem as SearchResult} onNavigate={closeDialog} />
        ) : (
          <div className="bg-background w-96 shrink-0 border-l" />
        )}
      </div>
    </CustomDialogContent>
  )
}
